import * as React from 'react';
import { View, TouchableOpacity, Switch, Platform } from 'react-native';
import { Text, Icon, Button, Input } from 'react-native-elements';
import { CountryPicker } from 'react-native-country-codes-picker';
import { Formik } from 'formik';
import moment from 'moment';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import ShoppingScreenProps from 'types/screens/ShoppingScreenProps';
import SuccessModal from 'components/SuccessModal';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import { styles } from 'styles/screens/accountBalanceScreen';
import { ScrollView } from 'react-native-gesture-handler';
import accountBalanceValidationSchema from 'forms/schemas/accountBalanceForm';
import useOrientationPortrait from 'hooks/useOrientationPortrait';
import { depositBalance } from 'services/accountBalanceService';
import { useTranslation } from 'react-i18next';

export default ({ navigation }: ShoppingScreenProps): JSX.Element => {
  const { deleteAuthData } = useChangeAuthData();
  const mediumScreen = useMediumScreen();
  const orientationPort = useOrientationPortrait();
  const { setModalData, setModalizeData } = React.useContext(AppContext);
  const { currentVenue } = React.useContext(OrderContext);
  const [showInvalidCodeMessage, setShowInvalidCodeMessage] = React.useState<boolean>(false);
  const [showCountryPicker, setShowCountryPicker] = React.useState(false);
  const [countryCode, setCountryCode] = React.useState('+54');
  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm') : null;

  const { t } = useTranslation();

  const ModalMessageContent = (success: boolean): JSX.Element => (
    <SuccessModal
      isSuccess={success}
      title={t('balance-load')}
      mainColor={currentVenue?.appColor ?? '#001C47'}
      message={t('balance-load-success')}
      buttonText={success ? t('accept') : t('retry-order')}
      onConfirm={() => {
        setModalizeData(null);
        if (success) {
          navigation.goBack();
        }
      }}
      isMediumScreen={mediumScreen}
    />
  );

  const onSubmit = async (phone: string, amount: number, isCash: boolean, isPosnet: boolean): Promise<void> => {
    if (!currentVenue) return;
    let response = false;
    phone = `${countryCode.replace('+', '')}${phone}`;

    response = await depositBalance(
      phone,
      amount,
      currentVenue.id,
      isCash,
      isPosnet,
      currentVenue.localDateTimeIdentifier,
    );

    if (response) {
      setModalizeData({
        content: ModalMessageContent(true),
        closeOnOverlayTap: false,
      });
    } else {
      setShowInvalidCodeMessage(true);
    }
  };

  return (
    <View style={styles.container}>
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          tvParallaxProperties={null}
          name="arrow-back-outline"
          type="ionicon"
          color="#001C47"
          size={40}
          containerStyle={mediumScreen ? styles.goBackContainerLarge : styles.goBackContainerSmall}
          onPress={() => {
            navigation.goBack();
          }}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLargeOrders}>
              <Text
                style={[styles.textTitleLargeOrders, styles.textTitleLargeCommerce]}
              >{`${currentVenue?.name} >`}</Text>
              {` ${t('balance-reload')}`}
            </Text>
            <View style={{ marginTop: 25, marginLeft: 60 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: '400',
                  lineHeight: 20,
                  letterSpacing: 0.25,
                  textAlign: 'left',
                  color: '#001639',
                }}
              >{`${currentDateTime}hs`}</Text>
            </View>
          </View>
        ) : (
          <Text style={mediumScreen ? styles.textLarge : styles.textSmall}>{currentVenue?.name}</Text>
        )}
        <Icon
          tvParallaxProperties={null}
          name="person-circle-sharp"
          type="ionicon"
          color="#001639"
          size={60}
          containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
          onPress={() =>
            setModalData({
              description: t('logout'),
              generalColor: currentVenue?.appColor ?? '#001C47',
              action: async () => {
                await deleteAuthData();
              },
            })
          }
        />
      </View>
      {!mediumScreen && (
        <Text
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignSelf: 'flex-start',
            marginLeft: 40,
            marginBottom: 10,
            fontSize: 16,
            fontWeight: '500',
            lineHeight: 24,
            letterSpacing: 0,
            textAlign: 'left',
          }}
        >
          {t('balance-reload')}
        </Text>
      )}
      <ScrollView style={styles.scrollItemsContainer} contentContainerStyle={styles.contentContainerStyleScrollView}>
        {showInvalidCodeMessage && <Text style={styles.invalidSubmit}>{t('error-occurred')}</Text>}
        <Formik
          validationSchema={accountBalanceValidationSchema}
          initialValues={{ phone: '', amount: '', isCash: false, isPosnet: false }}
          onSubmit={(values) => onSubmit(values.phone, Number(values.amount), values.isCash, values.isPosnet)}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            isValid,
            touched,
            setFieldValue,
            isSubmitting,
          }) => (
            <>
              <View
                style={mediumScreen || orientationPort ? styles.formContainerPortrait : styles.formContainerLandscape}
              >
                <View style={styles.countryCodephoneInputContainer}>
                  <TouchableOpacity
                    onPress={() => setShowCountryPicker(true)}
                    style={[styles.touchableCountryCodeContainer, { borderColor: currentVenue?.appColor ?? '#001C47' }]}
                    disabled={isSubmitting}
                  >
                    <Icon
                      tvParallaxProperties={null}
                      name="chevron-down-outline"
                      type="ionicon"
                      color="#001639"
                      style={styles.openCountryCodesIcon}
                    />
                    <Text style={styles.countryCodeText}>{countryCode}</Text>
                  </TouchableOpacity>
                  <Input
                    placeholder={t('phone')}
                    onChangeText={handleChange('phone')}
                    onBlur={handleBlur('phone')}
                    value={values.phone}
                    inputContainerStyle={[
                      mediumScreen ? styles.inputContainerLarge : styles.inputContainerSmall,
                      { borderColor: currentVenue?.appColor ?? '#001C47' },
                    ]}
                    inputStyle={styles.input}
                    placeholderTextColor="#979797"
                    disabled={isSubmitting}
                  />

                  <CountryPicker
                    lang="es"
                    show={showCountryPicker}
                    // when picker button press you will get the country object with dial code
                    pickerButtonOnPress={(item) => {
                      setCountryCode(item.dial_code);
                      setShowCountryPicker(false);
                    }}
                    disableBackdrop={isSubmitting}
                    style={{
                      modal: {
                        height: '80%',
                      },
                    }}
                    inputPlaceholder={t('search-your-country')}
                    onBackdropPress={() => setShowCountryPicker(false)}
                  />
                </View>
                {errors.phone && touched.phone && <Text style={styles.errorText}>{errors.phone}</Text>}
                <Input
                  placeholder={t('amount')}
                  onChangeText={handleChange('amount')}
                  onBlur={handleBlur('amount')}
                  value={values.amount}
                  inputContainerStyle={[
                    mediumScreen ? styles.inputContainerLarge : styles.inputContainerSmall,
                    { borderColor: currentVenue?.appColor ?? '#001C47' },
                  ]}
                  inputStyle={styles.input}
                  placeholderTextColor="#979797"
                  keyboardType="numeric"
                  disabled={isSubmitting}
                />
                {errors.amount && touched.amount && <Text style={styles.errorText}>{errors.amount}</Text>}
                <View style={styles.switchesContainer}>
                  <View style={styles.switchIsCashContainer}>
                    <Text style={styles.switchTitle}>{t('is-cash-payment')}</Text>
                    <Switch
                      trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                      thumbColor={'#001639'}
                      onValueChange={() => {
                        setFieldValue('isCash', !values.isCash);
                      }}
                      {...Platform.select({
                        web: {
                          activeThumbColor: currentVenue?.appColor,
                        },
                      })}
                      value={values.isCash}
                      style={styles.switch}
                      disabled={isSubmitting}
                    />
                  </View>
                  <View style={styles.switchIsPosnetContainer}>
                    <Text style={styles.switchTitle}>{t('is-posnet-payment')}</Text>
                    <Switch
                      trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                      thumbColor={'#001639'}
                      onValueChange={() => {
                        setFieldValue('isPosnet', !values.isPosnet);
                      }}
                      {...Platform.select({
                        web: {
                          activeThumbColor: currentVenue?.appColor,
                        },
                      })}
                      value={values.isPosnet}
                      style={styles.switch}
                      disabled={isSubmitting}
                    />
                  </View>
                </View>
                {errors.isCash && <Text style={styles.errorText}>{errors.isCash}</Text>}
                {errors.isPosnet && <Text style={styles.errorText}>{errors.isPosnet}</Text>}
                <Button
                  onPress={() => handleSubmit()}
                  title={t('reload')}
                  type="solid"
                  buttonStyle={[styles.button, { backgroundColor: currentVenue?.appColor ?? '#001C47' }]}
                  titleStyle={styles.buttonTitle}
                  containerStyle={mediumScreen ? styles.buttonContainerLarge : styles.buttonContainerSmall}
                  disabled={!isValid || isSubmitting}
                />
              </View>
            </>
          )}
        </Formik>
      </ScrollView>
    </View>
  );
};
